import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Menu } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { SettingsDialog } from "./SettingsDialog";
import { AdminGuideDialog } from "./AdminGuideDialog";
import { ConsentInformativaDialog } from "./DeliveryConsent";

const COLLAPSE_KEY = "ss_sidebar_collapsed";

export const AppShell = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    }, [collapsed]);

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8FD]">
            <Sidebar
                open={mobileOpen}
                onToggle={() => setMobileOpen((v) => !v)}
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed((v) => !v)}
                onOpenSettings={() => setSettingsOpen(true)}
                onOpenGuide={() => setGuideOpen(true)}
                onLogout={handleLogout}
                user={user}
            />
            <AdminGuideDialog
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                userId={user?.id}
            />
            {/* Operatori = dipendenti amministrativi: l'informativa va firmata anche entrando come staff */}
            {user?.role === "operator" && <ConsentInformativaDialog />}
            <SettingsDialog
                open={settingsOpen}
                onOpenChange={(o) => {
                    setSettingsOpen(o);
                    if (!o) {
                        // Notify pages (Dashboard) that settings may have changed
                        // so they can re-fetch firma_datore / rotate_back.
                        window.dispatchEvent(new Event("ss-settings-updated"));
                    }
                }}
            />

            {/* Main content area — leaves room for sidebar on desktop */}
            <div className={`flex min-w-0 flex-1 flex-col transition-all duration-200 ${collapsed ? "lg:pl-[72px]" : "lg:pl-64"}`}>
                {/* Mobile topbar with hamburger (visible only when sidebar collapsed) */}
                <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm lg:hidden">
                    <button
                        onClick={() => setMobileOpen(true)}
                        className="rounded-md p-2 text-slate-600 hover:bg-slate-100"
                        aria-label="Apri menu"
                        data-testid="topbar-menu-toggle"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <img src="/brand/logo-full.jpg" alt="Splendor Sud" className="h-7 object-contain" />
                        <span className="hidden text-[10px] font-semibold uppercase tracking-widest text-slate-400 sm:inline">Portale HR</span>
                    </div>
                    <div className="w-9" /> {/* spacer for symmetry */}
                </header>

                <main className="flex-1" data-testid="app-main">
                    {children}
                </main>
            </div>
        </div>
    );
};
